// #32 — wall playback lives in the floating dock (spec 2026-10-20 wall sheet items §#32): one wall card at a
// time (playing another wall item replaces it), surviving navigation; it never pops out to PiP.
import { create, type StoreApi, type UseBoundStore } from "zustand";
import type { WallItem, WallPost } from "../api/content";
import { recentOf } from "../lib/recentPlays";
import { isDockPlayable, kindOf } from "../player";
import { useAuthStore } from "./authStore";
import { useChatLinkPlaybackStore } from "./chatLinkPlaybackStore";
import { useRecentPlaysStore } from "./recentPlaysStore";

/**
 * Items the dock can play: its kind is dockable (YouTube by url or video id, SoundCloud, audio files and radio
 * streams), has something to load, and this browser can decode it (an Ogg radio on Safari is "Can't play here").
 */
export const isWallPlayable = (i: WallItem): boolean => isDockPlayable(i);

/** Embeds (YouTube / SoundCloud) play in the dock's visible hosted player; the rest in the AudioPlayer. */
export const isLinkItem = (i: WallItem): boolean => kindOf(i).surface === "embed";

/**
 * Held (Android `wallItemHeld`): an item that must not play unseen (YouTube / SoundCloud: `heldWhenHidden`) whose
 * player is not wholly visible pauses until it shows again — hidden (another player has the mini player), covered by
 * an app overlay, or clipped (scrolled partly out of the dock, off screen). The mini player is not a reason: it keeps
 * a visible 200×200 player. Audio and radio play on.
 */
export function isWallItemHeld(item: WallItem, why: { hidden: boolean; covered: boolean; clipped: boolean }): boolean {
  return kindOf(item).caps.heldWhenHidden && (why.hidden || why.covered || why.clipped);
}

/** The nearest playable index after (`dir` 1) or before (-1) `from`; null when there is none. */
export function nextPlayable(items: WallItem[], from: number, dir: 1 | -1): number | null {
  for (let i = from + dir; i >= 0 && i < items.length; i += dir) {
    if (isWallPlayable(items[i])) return i;
  }
  return null;
}

export interface WallPlayback {
  post: WallPost;
  wallName: string | null;
  index: number;
  paused: boolean;
  failed: boolean;
}

export interface WallPlaybackState {
  current: WallPlayback | null;
  /** Replaces any current card; index = the item's index in post.items. An unknown or unplayable item does nothing. */
  play(post: WallPost, itemId: string, wallName: string | null): void;
  togglePause(): void;
  /** The nearest playable item after / before; none → unchanged. */
  next(): void;
  prev(): void;
  /** The item finished: next(); at the last playable item → paused, index unchanged. */
  ended(): void;
  /** The item cannot play: failed and paused (the card offers Play, which retries); next / prev / play / togglePause clear it. */
  failed(): void;
  stop(): void;
  /** Stops the card when it plays this post (the post was deleted). */
  stopPost(postId: string): void;
  reset(): void;
}

export const useWallPlaybackStore: UseBoundStore<StoreApi<WallPlaybackState>> = create<WallPlaybackState>()((set, get) => {
  const patch = (p: Partial<WallPlayback>) => {
    const cur = get().current;
    if (cur) set({ current: { ...cur, ...p } });
  };
  const step = (dir: 1 | -1): boolean => {
    const cur = get().current;
    if (!cur) return false;
    const to = nextPlayable(cur.post.items, cur.index, dir);
    if (to == null) return false;
    patch({ index: to, paused: false, failed: false });
    // #32 Recent plays: the current item changed.
    useRecentPlaysStore.getState().record(recentOf(cur.post, cur.wallName, Date.now()));
    return true;
  };
  return {
    current: null,
    play(post, itemId, wallName) {
      const index = post.items.findIndex((i) => i.id === itemId);
      if (index < 0 || !isWallPlayable(post.items[index])) return;
      // Link kinds step 2: the wall card and a chat link never play together.
      useChatLinkPlaybackStore.getState().stop();
      set({ current: { post, wallName, index, paused: false, failed: false } });
      useRecentPlaysStore.getState().record(recentOf(post, wallName, Date.now()));
    },
    togglePause() {
      const cur = get().current;
      if (cur) patch({ paused: !cur.paused, failed: false });
    },
    next() {
      step(1);
    },
    prev() {
      step(-1);
    },
    ended() {
      if (!step(1)) patch({ paused: true });
    },
    failed() {
      patch({ failed: true, paused: true });
    },
    stop() {
      set({ current: null });
    },
    stopPost(postId) {
      if (get().current?.post.id === postId) set({ current: null });
    },
    reset() {
      set({ current: null });
    },
  };
});

// Logout (every path ends in clearAuth) or another account: the next user never inherits this playback.
// Same signal as the react-query cache clear (lib/queryCacheOnLogout.ts).
useAuthStore.subscribe((s, prev) => {
  if ((prev.username && !s.username) || (prev.userId != null && s.userId !== prev.userId)) useWallPlaybackStore.getState().reset();
});

// A chat link started playing in the dock: the wall card stops (one dock player for both, Android `playChatLink`).
useChatLinkPlaybackStore.subscribe((s, prev) => {
  if (s.current && s.current.np !== prev.current?.np) useWallPlaybackStore.getState().stop();
});
